import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { COLLECTIONS } from '../data/mock';
import { useCatalog } from '../context/CatalogContext';
import { useTranslation } from '../hooks/useTranslation';

function Reveal({ children, delay = 0 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { el.classList.add('visible'); io.unobserve(el); } });
    }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <div ref={ref} className="reveal" style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
}

export default function Collections() {
  const { products } = useCatalog();
  const { t } = useTranslation();
  return (
    <main>
      {/* Header (compact) */}
      <section className="pt-8 pb-6 md:pt-12 md:pb-10 bg-ivory text-center">
        <div className="max-w-3xl mx-auto px-4 animate-fade-up">
          <div className="text-[10px] md:text-[11px] tracking-luxury uppercase text-gold mb-3">{t('collections.label')}</div>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-light">{t('collections.title')}</h1>
          <p className="mt-3 md:mt-4 text-[#666] tracking-luxury text-[10px] md:text-xs uppercase">{t('collections.tagline')}</p>
        </div>
      </section>

      {/* Collection cards — 2 on mobile / 3 on desktop */}
      <section className="pb-16 lg:pb-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-3 md:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6 lg:gap-8">
          {COLLECTIONS.map((c, i) => {
            const count = products.filter((p) => p.collection === c.slug).length;
            return (
              <Reveal key={c.slug} delay={i * 60}>
                <Link to={`/collections/${c.slug}`} className="group block relative aspect-[3/4] overflow-hidden bg-white zoom-parent" data-testid={`collections-card-${c.slug}`}>
                  <img src={c.cover} alt={c.name} className="w-full h-full object-contain p-2 md:p-6 bg-white" />
                  <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none" />
                  <div className="absolute inset-x-0 bottom-0 p-3 md:p-6 text-white">
                    <div className="font-serif text-lg md:text-3xl lg:text-4xl leading-tight">{c.name}</div>
                    <div className="flex items-center justify-between mt-2 md:mt-3 gap-2">
                      <span className="text-[8px] md:text-[10px] tracking-[0.22em] md:tracking-[0.32em] uppercase opacity-75 whitespace-nowrap">{count > 0 ? `${count} ${t('collections.models')}` : t('collections.soon')}</span>
                      <span className="inline-flex items-center gap-1 md:gap-2 text-[8px] md:text-[10px] tracking-[0.22em] md:tracking-[0.32em] uppercase opacity-90 group-hover:gap-2 md:group-hover:gap-3 transition-all duration-500 whitespace-nowrap">
                        {t('collections.discover')} <ArrowRight className="w-2.5 h-2.5 md:w-3 md:h-3" />
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>
    </main>
  );
}
